// ui/web/src/components/personal/AccessRevokedModal.tsx
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { SvgAlertTriangle as AlertTriangleIcon } from "@opal/icons"
import type { ShareEvent } from "@/shared/hooks/useShareEvents"

interface AccessRevokedModalProps {
  event: ShareEvent
  onClose: () => void
}

export function AccessRevokedModal({ event, onClose }: AccessRevokedModalProps) {
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(30)

  // Countdown to auto-dismiss
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          onClose()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="revoke-title"
    >
      <div className="bg-background-neutral-00 rounded-xl border border-[var(--border-01)] shadow-xl p-6 w-96 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangleIcon className="size-5 text-amber-600" aria-hidden />
          </div>
          <div>
            <h2 id="revoke-title" className="font-semibold text-[var(--text-01)]">Access Revoked</h2>
            <p className="text-xs text-[var(--text-02)]">Revoked by {event.actorName}</p>
          </div>
        </div>

        {/* Body */}
        <p className="text-sm text-[var(--text-02)]">
          <strong className="text-[var(--text-01)] font-medium">"{event.resourceName}"</strong>{" "}
          is no longer shared with you. The resource has been removed from your access.
        </p>

        {/* Countdown */}
        <p className="text-xs text-[var(--text-02)]">
          This dialog will close automatically in <strong>{countdown} seconds</strong>.
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            id="btn-revoke-dismiss"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
          >
            Dismiss
          </button>
          <button
            id="btn-revoke-ok"
            className="btn btn-primary btn-sm"
            onClick={() => {
              onClose()
              navigate("/shared-with-me", { replace: true })
            }}
          >
            Go to Shared With Me
          </button>
        </div>
      </div>
    </div>
  )
}
